// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    invoices: {
      label: 'Facturas emitidas',
      help: 'Una fila por factura emitida. Los borradores y las anuladas no cuentan como facturación',
    },
    dates: {
      issue: 'Fecha de emisión',
      due: 'Vencimiento',
      paid: 'Fecha de cobro',
    },
    metrics: {
      invoiced: 'Facturado con IVA',
      net: 'Facturado',
      tax: 'IVA repercutido',
      invoices: 'Facturas',
      average: 'Factura media',
      collected: 'Cobrado',
      outstanding: 'Pendiente',
      overdue: 'Vencido',
      daysToPay: 'Días hasta cobro',
      cost: 'Coste',
      margin: 'Margen',
    },
    dimensions: {
      customer: 'Cliente',
      project: 'Obra',
      status: 'Estado',
      series: 'Serie',
      paymentMethod: 'Forma de pago',
      source: 'Origen',
      currency: 'Moneda',
    },
    filters: {
      total: 'Total de la factura',
      taxId: 'NIF del cliente',
    },
  },

  ca: {
    invoices: {
      label: 'Factures emeses',
      help: 'Una fila per factura emesa. Els esborranys i les anul·lades no compten com a facturació',
    },
    dates: {
      issue: 'Data d\'emissió',
      due: 'Venciment',
      paid: 'Data de cobrament',
    },
    metrics: {
      invoiced: 'Facturat amb IVA',
      net: 'Facturat',
      tax: 'IVA repercutit',
      invoices: 'Factures',
      average: 'Factura mitjana',
      collected: 'Cobrat',
      outstanding: 'Pendent',
      overdue: 'Vençut',
      daysToPay: 'Dies fins al cobrament',
      cost: 'Cost',
      margin: 'Marge',
    },
    dimensions: {
      customer: 'Client',
      project: 'Obra',
      status: 'Estat',
      series: 'Sèrie',
      paymentMethod: 'Forma de pagament',
      source: 'Origen',
      currency: 'Moneda',
    },
    filters: {
      total: 'Total de la factura',
      taxId: 'NIF del client',
    },
  },

  en: {
    invoices: {
      label: 'Issued invoices',
      help: 'One row per issued invoice. Drafts and cancelled ones are not turnover',
    },
    dates: {
      issue: 'Issue date',
      due: 'Due date',
      paid: 'Payment date',
    },
    metrics: {
      invoiced: 'Invoiced with VAT',
      net: 'Invoiced',
      tax: 'VAT charged',
      invoices: 'Invoices',
      average: 'Average invoice',
      collected: 'Collected',
      outstanding: 'Outstanding',
      overdue: 'Overdue',
      daysToPay: 'Days to payment',
      cost: 'Cost',
      margin: 'Margin',
    },
    dimensions: {
      customer: 'Customer',
      project: 'Job',
      status: 'Status',
      series: 'Series',
      paymentMethod: 'Payment method',
      source: 'Source',
      currency: 'Currency',
    },
    filters: {
      total: 'Invoice total',
      taxId: 'Customer tax id',
    },
  },

  fr: {
    invoices: {
      label: 'Factures émises',
      help: 'Une ligne par facture émise. Les brouillons et les annulées ne comptent pas comme chiffre d\'affaires',
    },
    dates: {
      issue: 'Date d\'émission',
      due: 'Échéance',
      paid: 'Date de paiement',
    },
    metrics: {
      invoiced: 'Facturé TTC',
      net: 'Facturé',
      tax: 'TVA collectée',
      invoices: 'Factures',
      average: 'Facture moyenne',
      collected: 'Encaissé',
      outstanding: 'En attente',
      overdue: 'Échu',
      daysToPay: 'Jours jusqu\'au paiement',
      cost: 'Coût',
      margin: 'Marge',
    },
    dimensions: {
      customer: 'Client',
      project: 'Chantier',
      status: 'Statut',
      series: 'Série',
      paymentMethod: 'Mode de paiement',
      source: 'Origine',
      currency: 'Devise',
    },
    filters: {
      total: 'Total de la facture',
      taxId: 'Numéro fiscal du client',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
